import { db, type Conflict, type SyncEntity } from './database'
import { writeLocal } from './sync'

/**
 * Trancher un conflit — décision 2026-08-24 (soir).
 *
 * Le moteur détecte et STOCKE les conflits depuis le début, mais rien ne les
 * présentait : ils s'accumulaient dans une table que personne ne lisait. Sur
 * une app d'argent, un désaccord qu'on ne montre pas est un désaccord qu'on
 * tranche au hasard.
 *
 * ⚠️ Aucune des deux versions n'est jamais perdue avant le choix. C'est ce qui
 * sépare un conflit d'un écrasement silencieux — le garde-fou de `vision.md` :
 * « jamais de last-write-wins silencieux ».
 */

/** Les champs qui portent de la mécanique, jamais du sens pour l'utilisateur. */
const PLOMBERIE = new Set([
  'id', 'wallet_id', '_version', 'client_updated_at', 'deleted_at',
  'entered_by_account_id',
])

/** Ce qu'un champ veut dire, en français. */
const LIBELLES: Record<string, string> = {
  amount: 'Montant',
  direction: 'Sens',
  operation_date: 'Date',
  title: 'Titre',
  note: 'Description',
  category_id: 'Catégorie',
  name: 'Nom',
  cycle: 'Rythme',
  expected_income: 'Revenu attendu',
  effective_from: 'À partir du',
  plan_mode: 'Mode de dépense',
  carries_over: 'Cumulative',
  planned_date: 'Journée prévue',
  planned_amount: 'Montant prévu',
  label: 'Libellé',
  confirmed_at: 'Confirmée le',
  transaction_id: 'Dépense liée',
  position: 'Position',
  icon: 'Icône',
  target_amount: 'Montant visé',
  target_date: 'Échéance',
  moved_on: 'Date du mouvement',
  goal_id: 'Objectif',
  manual_category_order: 'Ordre choisi à la main',
  archived_at: 'Rangé le',
  achieved_at: 'Atteint le',
}

/** Ce que l'entité désigne, pour nommer le conflit sans jargon. */
const ENTITES: Record<string, string> = {
  transactions: 'Une opération',
  categories: 'Une catégorie',
  budgets: 'Une enveloppe',
  budget_allocations: 'Une journée prévue',
  wallets: 'Le portefeuille',
  goals: 'Un objectif',
  goal_deposits: 'Un versement',
}

export interface FieldDiff {
  field: string
  label: string
  mine: unknown
  theirs: unknown
}

export interface ConflictView {
  conflict: Conflict
  /** Ce que l'entité désigne, en français. */
  subject: string
  /**
   * Les champs qui DIFFÈRENT, et eux seuls.
   *
   * Afficher les deux lignes entières obligerait à comparer trente valeurs à
   * l'œil pour en trouver une. Un écran de résolution qui demande ce travail
   * est un écran qu'on referme.
   */
  differences: FieldDiff[]
}

/** Les conflits en attente, les plus récents d'abord, prêts à être présentés. */
export async function pendingConflicts(): Promise<ConflictView[]> {
  const rows = await db.conflicts.toArray()
  return rows
    .sort((a, b) => b.detected_at.localeCompare(a.detected_at))
    .map((conflict) => ({
      conflict,
      subject: ENTITES[conflict.entity] ?? 'Une donnée',
      differences: diff(conflict.local_version, conflict.server_version),
    }))
}

function diff(mine: unknown, theirs: unknown): FieldDiff[] {
  const a = (mine ?? {}) as Record<string, unknown>
  const b = (theirs ?? {}) as Record<string, unknown>
  const champs = new Set([...Object.keys(a), ...Object.keys(b)])

  return [...champs]
    .filter((field) => !PLOMBERIE.has(field))
    // `?? null` : un champ ajouté après coup vaut `undefined` sur les lignes
    // déjà écrites, et `undefined !== null` ferait apparaître une différence
    // là où il n'y en a pas.
    .filter((field) => normalise(a[field]) !== normalise(b[field]))
    .map((field) => ({
      field,
      label: LIBELLES[field] ?? field,
      mine: a[field] ?? null,
      theirs: b[field] ?? null,
    }))
}

const normalise = (value: unknown) => JSON.stringify(value ?? null)

export type Choice = 'mine' | 'theirs'

/**
 * Applique le choix, et referme le conflit.
 *
 * **Garder la mienne** réécrit la ligne locale par `writeLocal`, ce qui pose une
 * horloge client NEUVE et la remet dans la file. Sans cette horloge plus
 * récente, le serveur refuserait à nouveau — l'arbitrage compare
 * `client_updated_at` — et le même conflit rouvrirait indéfiniment.
 *
 * **Prendre la sienne** écrit directement dans Dexie, SANS passer par la file :
 * la ligne vient du serveur, la lui renvoyer n'aurait aucun sens. On pose sa
 * version pour que le prochain envoi parte d'une base juste.
 */
export async function resolveConflict(id: string, choice: Choice): Promise<void> {
  const conflict = await db.conflicts.get(id)
  if (!conflict) return

  const table = (db as unknown as Record<string, {
    put: (row: unknown) => Promise<unknown>
  }>)[conflict.entity]
  if (!table) return

  if (choice === 'mine') {
    const mine = conflict.local_version as Record<string, unknown> | null
    if (mine) await writeLocal(conflict.entity as SyncEntity, mine as { id: string })
  } else {
    const theirs = conflict.server_version as Record<string, unknown> | null
    if (theirs) {
      await table.put({ ...theirs, _version: conflict.server_row_version ?? 0 })
    }
  }

  await db.conflicts.delete(id)
}

/**
 * Trancher tous les conflits d'un coup, dans le même sens.
 *
 * Proposé parce qu'un rattrapage après une longue coupure peut en produire
 * beaucoup, et qu'on ne va pas demander trente fois la même chose. Jamais
 * proposé par défaut, en revanche : le choix reste explicite.
 */
export async function resolveAll(choice: Choice): Promise<number> {
  const rows = await db.conflicts.toArray()
  for (const row of rows) await resolveConflict(row.id, choice)
  return rows.length
}
